import { EllipsisOutlined, FilterOutlined } from '@ant-design/icons';
import { Button, Card, DatePicker, Dropdown, Form, Menu, Modal, Select, Space, Table, Tag, Typography, message } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { Field, Grid, GridRow, Integration, IntegrationExecution } from '../../../models';
import { apiClient, apiErrorMessage, getApiBaseUrl } from '../../../utils';
import { DataPreviewPage } from './data-preview-page';
import { ExecuteWithFilePage } from './execute-with-file-page';
import { ExecutionRecordsPage } from './execution-records-page';

const operatorMap = [
  { value: '==', name: 'eşit' },
  { value: '!=', name: 'eşit değil' },
  { value: '>', name: 'büyük' },
  { value: '>=', name: 'büyük eşit' },
  { value: '<', name: 'küçük' },
  { value: '<=', name: 'küçük eşit' },
  { value: 'in', name: 'içinde' },
  { value: '!in', name: 'içinde değil' },
  { value: 'startsWith', name: 'başlar' },
  { value: 'endsWith', name: 'biter' },
  { value: 'contains', name: 'içerir' },
  { value: 'null', name: 'boş' },
  { value: '!null', name: 'boş değil' },
];

export function IntegrationExecutionsGrid(props: { integration?: Integration }) {
  const { Text } = Typography;

  const [loading, setLoading] = useState(false);
  const [executionRunning, setExecutionRunning] = useState(false);
  const [executions, setExecutions] = useState<(IntegrationExecution & { key: string })[]>();
  const [showPreview, setShowPreview] = useState(false);
  const [executeWithFileModalOpen, setExecuteWithFileModalOpen] = useState(false);
  const [recordsModalOpen, setRecordsModalOpen] = useState<IntegrationExecution>();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterValues, setFilterValues] = useState<any>(undefined);
  const [grid, setGrid] = useState<Grid>();
  const [fkRecords, setFkRecords] = useState<GridRow[]>();
  const [tableCurrent, setTableCurrent] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(10);
  const [tableTotal, setTableTotal] = useState(0);

  const loadExecutions = useCallback(() => {
    setLoading(true);
    let offset = (tableCurrent - 1) * tablePageSize;
    const queryParams = [`limit=${tablePageSize}`, `offset=${offset}`];

    if (filterValues) {
      if (filterValues.resultStatus && filterValues.resultStatus !== 'NON_EMPTY') {
        queryParams.push(`resultStatus=${filterValues.resultStatus}`);
      }
      if (filterValues.dateRange) {
        queryParams.push(`from=${filterValues.dateRange[0].format('yyyy-MM-DD')}`);
        queryParams.push(`to=${filterValues.dateRange[1].format('yyyy-MM-DD')}`);
      }
      if (filterValues.executor && filterValues.executor !== 'ALL') {
        queryParams.push(`executor=${filterValues.executor}`);
      }
    }

    const queryParamsStr = queryParams.join('&');
    apiClient
      .get(`integrations/${props.integration?.id}/executions?${queryParamsStr}`)
      .then(response => {
        const responseExecutions: IntegrationExecution[] = response.data.rows;
        const responseData = responseExecutions.map(ie => {
          return {
            ...ie,
            key: ie.id,
          };
        });

        if (!response.data.count) {
          setTableTotal(0);
        }

        setTableTotal(response.data.count);
        setExecutions(responseData);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [filterValues, props.integration?.id, tableCurrent, tablePageSize]);

  const loadGrid = useCallback(() => {
    apiClient
      .get(`grids/${props.integration?.primaryGridId}`)
      .then(response => {
        setGrid(response.data);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      });
  }, [props.integration?.primaryGridId]);

  const loadFkRecords = useCallback(async () => {
    if (!executions?.length || !grid) {
      return;
    }

    const newFkRecords: GridRow[] = [];

    for (const execution of executions) {
      if (!execution.filter?.length) {
        continue;
      }

      for (const rule of execution.filter) {
        const field = grid?.fields.find((f: Field) => f.id === rule.fieldId);
        if (field?.dataType === 'FOREIGNKEY' || field?.dataType === 'MULTIFOREIGNKEY') {
          const fkGridId = field.foreignKeyGridId;
          const fkRecordIds = Array.isArray(rule.value) ? rule.value : [rule.value];

          for (const fkRecordId of fkRecordIds) {
            if (newFkRecords.find(r => r.id === fkRecordId)) {
              continue;
            }

            await apiClient
              .get(`records/record?gridId=${fkGridId}&rowId=${fkRecordId}`)
              .then((response: any) => {
                newFkRecords.push(response.data);
              })
              .catch(() => {});
          }
        }
      }
    }

    setFkRecords(newFkRecords?.length ? newFkRecords : undefined);
  }, [executions, grid]);

  const preview = () => {
    setShowPreview(true);
  };

  const closePreview = () => {
    setShowPreview(false);
  };

  const openExecuteWithFileModal = () => {
    setExecuteWithFileModalOpen(true);
  };

  const closeExecuteWithFileModal = () => {
    setExecuteWithFileModalOpen(false);
  };

  const onExecuteWithFileCompleted = () => {
    closeExecuteWithFileModal();
    loadExecutions();
  };

  const openRecordsModal = (integrationExecution: IntegrationExecution) => {
    setRecordsModalOpen(integrationExecution);
  };

  const closeRecordsModal = () => {
    setRecordsModalOpen(undefined);
  };

  const onRecordsClick = (integrationExecution: IntegrationExecution) => {
    openRecordsModal(integrationExecution);
  };

  const onFiltersToggle = () => {
    setFiltersOpen(!filtersOpen);
  };

  const onFiltersChange = (filterValues: any) => {
    setFilterValues(filterValues);
  };

  const redirectToExcelExportTab = () => {
    const queryParams: string[] = [];

    if (filterValues) {
      if (filterValues.resultStatus && filterValues.resultStatus !== 'NON_EMPTY') {
        queryParams.push(`resultStatus=${filterValues.resultStatus}`);
      }
      if (filterValues.dateRange) {
        queryParams.push(`from=${filterValues.dateRange[0].format('yyyy-MM-DD')}`);
        queryParams.push(`to=${filterValues.dateRange[1].format('yyyy-MM-DD')}`);
      }
    }
    const queryParamsStr = queryParams.join('&');

    const baseApiUrl = getApiBaseUrl() + '/api/';
    const url = `${baseApiUrl}integrations/${props.integration?.id}/executions/export?${queryParamsStr}`;
    window.open(url, '_blank');
  };

  const onActionsClick = (e: any) => {
    if (e.key === 'import') {
      openExecuteWithFileModal();
    } else if (e.key === 'excelExport') {
      redirectToExcelExportTab();
    }
  };

  const onErrorStackClick = (stack: string) => {
    Modal.confirm({
      title: 'Hata detayları',
      content: stack,
      okText: 'Tamam',
      cancelText: <></>,
      width: '30%',
    });
  };

  const onTableChange = (pagination: any) => {
    setTableCurrent(pagination.current);
    setTablePageSize(pagination.pageSize);
  };

  const actionsMenu = (
    <Menu onClick={onActionsClick}>
      <Menu.Item key="import">Dosyadan içe aktar</Menu.Item>
      <Menu.Item key="excelExport">Excel'e aktar</Menu.Item>
    </Menu>
  );

  const runExecution = () => {
    setExecutionRunning(true);
    apiClient
      .get(`integrations/${props.integration?.id}/execute`)
      .then(response => {
        const execution: IntegrationExecution = response.data;
        if (execution?.totalRecordCount === 0 && !execution.hasError) {
          message.info('Aktarım başlatıldı ancak aktarılacak kayıt bulunamadı.');
        } else {
          if (execution.hasError) {
            message.error(execution.errorMessage);
          }
          loadExecutions();
        }
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setExecutionRunning(false);
      });
  };

  useEffect(() => {
    loadExecutions();
    loadGrid();
  }, [loadExecutions, loadGrid]);

  useEffect(() => {
    loadFkRecords();
  }, [loadFkRecords]);

  const tableColumns: any[] = [
    {
      title: 'Aktarım zamanı',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: string) => <Text strong>{moment(createdAt).format('DD.MM.yyyy HH:mm:ss')}</Text>,
    },
    {
      title: 'Sonuç',
      dataIndex: 'hasError',
      key: 'hasError',
      render: (hasError: boolean, ie: IntegrationExecution) => (
        <>
          <Text type={hasError ? 'danger' : 'success'}>{hasError ? ie.errorMessage || 'Hata' : 'Tamamlandı'}</Text>
          {(hasError || ie.errorMessage) && (
            <Button type="link" onClick={() => onErrorStackClick(ie.errorStack)}>
              Detaylar
            </Button>
          )}
        </>
      ),
    },
    {
      title: 'Çalışma şekli',
      dataIndex: 'isManuallyTriggered',
      key: 'trigger',
      render: (isManuallyTriggered: boolean, ie: IntegrationExecution) =>
        isManuallyTriggered ? 'Elle (' + ie.executedBy?.fullName + (ie.isFileImport ? ', dosyadan aktarım' : '') + ')' : 'Otomatik',
    },
  ];

  if (props.integration?.integrationType === 'EXPORT') {
    tableColumns.push({
      title: 'Filtre',
      dataIndex: 'filter',
      key: 'filter',
      render: (filter: any[]) => (
        <Space direction="vertical">
          {filter?.length ? (
            filter?.map((item: any, i: number) => {
              const { fieldId, op, value } = item;
              const field = grid?.fields.find((f: Field) => f.id === fieldId);
              const fieldLabel = field?.name;
              const opLabel = operatorMap.find((o: any) => o.value === op)?.name;
              let valueDisplay = value;
              if (field?.dataType === 'FOREIGNKEY') {
                const fkRecord = fkRecords?.find(r => r.id === value);
                if (fkRecord && field?.foreignKeyFieldId) {
                  if (Array.isArray(value)) {
                    valueDisplay = value.map(v => fkRecord.cells[field?.foreignKeyFieldId].value);
                  } else {
                    valueDisplay = fkRecord.cells[field?.foreignKeyFieldId].value;
                  }
                }
              }
              if (field?.dataType === 'MULTIFOREIGNKEY') {
                valueDisplay =
                  (value as string[])?.map(v => {
                    const fkRecord = fkRecords?.find(r => r.id === v);
                    if (fkRecord && field?.foreignKeyFieldId) {
                      return fkRecord.cells[field?.foreignKeyFieldId].value;
                    }
                    return v;
                  }) || value;
              } else if (field?.dataType === 'PICKLIST') {
                if (field?.picklistValues?.length) {
                  if (Array.isArray(value)) {
                    valueDisplay = value.map(v => field.picklistValues.find(p => p.id === v)?.val || v);
                  } else {
                    valueDisplay = field.picklistValues.find(p => p.id === value)?.val || value;
                  }
                }
              } else if (field?.dataType === 'MULTIPICKLIST') {
                if (field?.picklistValues?.length) {
                  valueDisplay =
                    (value as string[])?.map(v => {
                      const picklistValue = field.picklistValues.find(p => p.id === v);
                      return picklistValue ? picklistValue.val : v;
                    }) || value;
                }
              } else if (field?.dataType === 'BOOLEAN') {
                valueDisplay = value === true ? 'Evet' : 'Hayır';
              }

              if (Array.isArray(valueDisplay)) {
                valueDisplay = valueDisplay?.join(', ');
              }

              return (
                <p key={i} style={{ margin: 0 }}>
                  {fieldLabel} <Tag>{opLabel}</Tag>
                  {valueDisplay}
                </p>
              );
            })
          ) : (
            <span>Tümü</span>
          )}
        </Space>
      ),
    });
  }

  tableColumns.push({
    title: 'Kayıt sayısı',
    dataIndex: 'recordCount',
    key: 'recordCount',
    render: (recordCount: number, ie: IntegrationExecution) => (
      <Space>
        <Button type="link" onClick={() => onRecordsClick(ie)}>
          <Text type="success">{recordCount}</Text>
        </Button>
        {ie.totalRecordCount > 0 && recordCount !== ie.totalRecordCount && <span>({ie.totalRecordCount})</span>}
      </Space>
    ),
  });

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'flex-end', marginBottom: '2rem', textAlign: 'right' }}>
        <Button key="filters" onClick={onFiltersToggle} icon={<FilterOutlined />}>
          Filtreleri {filtersOpen ? 'gizle' : 'göster'}
        </Button>
        {props.integration?.integrationType === 'IMPORT' && (
          <>
            <Button onClick={preview}>Veri önizleme</Button>
            <Button onClick={runExecution} loading={executionRunning}>
              Yeni aktarım başlat
            </Button>
            <Dropdown key="more" overlay={actionsMenu}>
              <Button type="text" style={{ padding: '0.25rem' }}>
                <EllipsisOutlined style={{ fontSize: '1.25rem', verticalAlign: 'top' }} />
              </Button>
            </Dropdown>
          </>
        )}
      </Space>
      <FiltersForm hidden={!filtersOpen} onChange={onFiltersChange} integrationId={props.integration?.id} />
      <Table
        columns={tableColumns}
        dataSource={executions}
        loading={loading}
        pagination={{
          current: tableCurrent,
          total: tableTotal,
          pageSize: tablePageSize,
          pageSizeOptions: ['10', '25', '50', '100'],
          position: ['bottomCenter'],
          showTotal: (total, range) => `Toplam ${total} kayıt. ${range[0]}-${range[1]} arası gösteriliyor.`,
          showSizeChanger: true,
        }}
        onChange={onTableChange}
      />
      <Modal title="Veri önizleme" visible={showPreview} footer={null} onCancel={closePreview} destroyOnClose={true} width={'90%'}>
        <DataPreviewPage integrationId={props.integration?.id} />
      </Modal>
      <Modal
        title="Aktarım kayıtları"
        visible={!!recordsModalOpen}
        footer={null}
        onCancel={closeRecordsModal}
        destroyOnClose={true}
        width={'90%'}
      >
        <ExecutionRecordsPage integrationExecution={recordsModalOpen} />
      </Modal>
      <Modal
        title="Dosyadan içe aktar"
        visible={executeWithFileModalOpen}
        footer={null}
        onCancel={closeExecuteWithFileModal}
        destroyOnClose={true}
      >
        <ExecuteWithFilePage integration={props.integration} onComplete={onExecuteWithFileCompleted} />
      </Modal>
    </>
  );
}

function FiltersForm(props: { hidden: boolean; onChange: (filterValues: any) => void; integrationId?: string }) {
  const { Option } = Select;
  const { RangePicker } = DatePicker;
  const [form] = useForm();
  const [executors, setExecutors] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const loadExecutors = useCallback(() => {
    setLoading(true);
    apiClient
      .get(`integrations/${props.integrationId}/executors`)
      .then(response => {
        setExecutors(response.data);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [props.integrationId]);

  useEffect(() => {
    loadExecutors();
  }, [loadExecutors]);

  const onChange = () => {
    const values = form.getFieldsValue();
    props.onChange(values);
  };

  if (props.hidden) {
    return null;
  }

  return (
    <Card style={{ marginBottom: '2rem' }}>
      <Form form={form} layout="inline">
        <Form.Item name="resultStatus">
          <Select defaultValue="NON_EMPTY" placeholder="Aktarım sonucu" style={{ width: '12rem' }} onChange={onChange}>
            <Option value="NON_EMPTY">Kayıt olanlar</Option>
            <Option value="ERROR">Hatalı olanlar</Option>
            <Option value="ALL">Tümü</Option>
          </Select>
        </Form.Item>
        <Form.Item name="dateRange">
          <RangePicker onChange={onChange} />
        </Form.Item>
        <Form.Item name="executor">
          <Select placeholder="Çalıştıran" style={{ width: '12rem' }} onChange={onChange} loading={loading}>
            <Option value="ALL">Tümü</Option>
            {executors.map(e => (
              <Option key={e.id} value={e.id}>
                {e.fullName}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Card>
  );
}
